import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface AIPrompt {
  id?: number;
  stage: string;
  promptType: string;
  promptContent: string;
  description?: string;
  isActive: boolean;
}

interface AIPromptsManagerProps {
  stage: string;
  promptTypes: string[];
  stageName: string;
}

const PROMPT_DESCRIPTIONS: { [key: string]: string } = {
  'parse_vacancy_text': 'Используется для парсинга текстовых вакансий на этапе выбора вакансий',
  'system': 'Системный промпт для AI консультанта, определяет его поведение и стиль общения',
  'welcome': 'Приветственное сообщение, которое отправляется пользователю при первом обращении к консультанту',
  'extract_resume_data': 'Используется для извлечения структурированных данных резюме из диалога с консультантом',
  'improve_resume': 'Используется для улучшения резюме на основе вакансий и консультации',
  'match_vacancy': 'Используется для анализа совместимости резюме с вакансией',
  'generate_letter': 'Используется для генерации мотивационных писем под конкретные вакансии',
  'estimate_salary': 'Используется для расчета рекомендуемой зарплаты на основе резюме и вакансии',
  'advise': 'Системный промпт модалки «ИИ-консультант» на странице вакансии — управляет и кнопкой «Советы», и генерацией сопроводительного письма',
  'cover_letter_template': 'Шаблон автосообщения при клике «Сопроводительное письмо». Плейсхолдеры: {{role}} — должность, {{company_clause}} — " в компании X" или пусто, если компания неизвестна',
  'cover_letter_auto_apply': 'Сопроводительное для «Автооткликнуться» (admin-only бета) — текст уходит как есть в реальную форму на сайте работодателя, поэтому, в отличие от cover_letter_template, на выходе должен быть только готовый текст письма, без анализа/вариантов/вопросов. Те же плейсхолдеры: {{role}}, {{company_clause}}',
  'generate_report': 'Системный промпт карьерного отчёта (вердикт, сильные стороны, план интервью). Возвращает строгий JSON — названия полей менять нельзя, иначе парсинг на бэкенде сломается',
  'apply_fix': 'Системный промпт быстрых автоисправлений резюме (кнопка «Исправить автоматически» и адаптация под роль). Возвращает JSON по ключам из задачи — правило про company/title/dateFrom/dateTo важно не трогать, иначе бэкенд перезапишет факты резюме'
};

const AIPromptsManager: React.FC<AIPromptsManagerProps> = ({ stage, promptTypes, stageName }) => {
  const [prompts, setPrompts] = useState<{[type: string]: AIPrompt}>({});
  const [loading, setLoading] = useState(true);
  const [editingPrompts, setEditingPrompts] = useState<{[key: string]: string}>({});
  const [savingPrompts, setSavingPrompts] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    loadPrompts();
  }, [stage]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/ai-prompts?stage=${stage}`);
      if (response.data.success) {
        const promptsMap: {[type: string]: AIPrompt} = {};
        response.data.prompts.forEach((prompt: AIPrompt) => {
          if (prompt.isActive) {
            promptsMap[prompt.promptType] = prompt;
          }
        });
        setPrompts(promptsMap);
        
        // Инициализируем редакторы
        const editors: {[key: string]: string} = {};
        promptTypes.forEach(type => {
          const key = `${stage}:${type}`;
          editors[key] = promptsMap[type]?.promptContent || '';
        });
        setEditingPrompts(editors);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки промптов:', error);
      toast.error('Ошибка загрузки промптов');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async (promptType: string) => {
    const key = `${stage}:${promptType}`;
    const content = editingPrompts[key];
    
    if (!content || content.trim().length === 0) {
      toast.error('Промпт не может быть пустым');
      return;
    }
    
    try {
      setSavingPrompts(prev => ({...prev, [key]: true}));
      
      const existingPrompt = prompts[promptType];
      
      if (existingPrompt && existingPrompt.id) {
        const response = await api.put(`/admin/ai-prompts/${existingPrompt.id}`, {
          promptContent: content
        });
        
        if (response.data.success) {
          toast.success('Промпт успешно обновлен');
          await loadPrompts();
        } else {
          toast.error(response.data.message || 'Ошибка обновления промпта');
        }
      } else {
        toast.error('Промпт не найден');
      }
    } catch (error: any) {
      console.error('Ошибка сохранения промпта:', error);
      toast.error(error.response?.data?.message || 'Ошибка сохранения промпта');
    } finally {
      setSavingPrompts(prev => ({...prev, [key]: false}));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {promptTypes.map((promptType) => {
        const key = `${stage}:${promptType}`;
        const prompt = prompts[promptType];
        const content = editingPrompts[key] || '';
        const isSaving = savingPrompts[key] || false;
        
        return (
          <div key={promptType} className="bg-white rounded-lg shadow-md p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {promptType.replace(/_/g, ' ')}
              </h3>
              {PROMPT_DESCRIPTIONS[promptType] && (
                <p className="text-sm text-gray-600 mt-1">
                  {PROMPT_DESCRIPTIONS[promptType]}
                </p>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Содержимое промпта
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setEditingPrompts(prev => ({...prev, [key]: e.target.value}))}
                  rows={stage === 'motivation_letters' || stage === 'career_report' ? 28 : 12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Введите промпт..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => handleSavePrompt(promptType)}
                  disabled={isSaving || !content.trim()}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="h-5 w-5" />
                  <span>{isSaving ? 'Сохранение...' : 'Сохранить промпт'}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AIPromptsManager;


